import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import useMobileView from '../../hooks/useMobileView';
import { detectThemeSync } from '../../utils/themeUtils';

const ACCENT = '#4ce1b6';
const SELECTED_TAB_TEXT = '#042018';

export const FINANCE_SECTION_TABS = [
  {
    url: '/finance/',
    label: 'Finance Dashboard',
    shortLabel: 'Dashboard',
    icon: DashboardOutlinedIcon,
  },
  {
    url: '/finance/payments/',
    label: 'Payment List',
    shortLabel: 'Payments',
    icon: ReceiptLongOutlinedIcon,
  },
  {
    url: '/finance/ota-commission/',
    label: 'OTA Commission',
    shortLabel: 'OTA',
    icon: PercentIcon,
  },
  {
    url: '/finance/cash-counter/',
    label: 'Cash Counter',
    shortLabel: 'Cash',
    icon: PointOfSaleOutlinedIcon,
  },
  {
    url: '/finance/draft-invoice/',
    label: 'Draft Invoice',
    shortLabel: 'Draft',
    icon: DescriptionOutlinedIcon,
  },
];

function isFinanceTabActive(pathname, url) {
  if (url === '/finance/') {
    return pathname === '/finance' || pathname === '/finance/';
  }
  if (!url) return false;
  if (pathname === url) return true;
  if (url.endsWith('/') && pathname === url.slice(0, -1)) return true;
  if (!url.endsWith('/') && `${pathname}/` === url) return true;
  return pathname.startsWith(`${url}/`);
}

export default function FinanceSectionTabs({ isDark: isDarkProp, inline = false, sx = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompact } = useMobileView();
  const isDark = isDarkProp ?? detectThemeSync() === 'dark';

  const activeIndex = FINANCE_SECTION_TABS.findIndex((tab) => isFinanceTabActive(location.pathname, tab.url));
  const value = activeIndex >= 0 ? activeIndex : 0;
  const tabMinHeight = inline ? 36 : 44;
  const isMobileInline = inline && isCompact;

  return (
    <Box
      sx={{
        width: isMobileInline || !inline ? '100%' : 'auto',
        flexShrink: 0,
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: isMobileInline ? 'flex' : 'inline-flex',
          width: isMobileInline || !inline ? '100%' : 'auto',
          maxWidth: '100%',
          p: '3px',
          borderRadius: inline ? '12px' : '16px',
          border: `1px solid ${alpha(ACCENT, isDark ? 0.24 : 0.2)}`,
          background: isDark ? alpha('#ffffff', 0.04) : alpha('#ffffff', 0.92),
          boxShadow: inline
            ? 'none'
            : isDark
              ? '0 8px 24px rgba(0,0,0,0.22)'
              : '0 6px 18px rgba(15,23,42,0.06)',
        }}
      >
        <Tabs
          value={value}
          onChange={(_, index) => navigate(FINANCE_SECTION_TABS[index].url)}
          variant={isCompact ? 'fullWidth' : 'standard'}
          aria-label="Finance section navigation"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: tabMinHeight,
            width: isMobileInline || !inline ? '100%' : 'auto',
            '& .MuiTabs-flexContainer': {
              gap: '3px',
              width: isCompact ? '100%' : 'auto',
            },
          }}
        >
          {FINANCE_SECTION_TABS.map((tab, index) => {
            const Icon = tab.icon;
            const selected = value === index;
            return (
              <Tab
                key={tab.url}
                disableRipple
                icon={<Icon sx={{ fontSize: inline ? 16 : 18 }} />}
                iconPosition="start"
                label={isCompact ? tab.shortLabel : tab.label}
                sx={{
                  minHeight: tabMinHeight,
                  minWidth: isCompact ? 0 : inline ? 118 : 148,
                  flex: isCompact ? '1 1 0' : '0 1 auto',
                  px: { xs: 0.85, sm: inline ? 1.35 : 1.75 },
                  py: 0.65,
                  borderRadius: inline ? '9px' : '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: isMobileInline ? '0.75rem' : inline ? '0.78rem' : '0.84rem',
                  letterSpacing: 0.05,
                  color: isDark ? '#94a3b8' : '#64748b',
                  background: selected
                    ? 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)'
                    : 'transparent',
                  border: selected ? `1px solid ${alpha(ACCENT, 0.75)}` : '1px solid transparent',
                  boxShadow: selected && !inline ? '0 6px 16px rgba(36, 226, 178, 0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  '& .MuiTab-iconWrapper': {
                    marginRight: 0.45,
                    color: 'inherit',
                  },
                  '&.Mui-selected': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                    fontWeight: 800,
                  },
                  '&.Mui-selected .MuiTab-iconWrapper': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                  },
                  '&:hover': {
                    color: selected ? SELECTED_TAB_TEXT : ACCENT,
                    background: selected
                      ? 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)'
                      : alpha(ACCENT, isDark ? 0.1 : 0.08),
                  },
                  '&.Mui-selected:hover': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Box>
    </Box>
  );
}
